/**
 * KitAtendimentoPublicPage
 * Public page for viewing Kit de Atendimento via token
 *
 * @version 2025.1.0
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Paper,
} from '@mui/material';
import {
  LocalHospital as HospitalIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { kitService } from '../../services/kitService';
import { KitData } from '../../types/kit';
import { ExpirationBanner } from '../../components/kit/ExpirationBanner';
import { KitDocumentCard } from '../../components/kit/KitDocumentCard';
import { KIT_POLL_INTERVAL_MS, canRetryAfterError, shouldKeepPolling } from './kitPolling';

export const KitAtendimentoPublicPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Só quem acabou de criar o agendamento (AppointmentFormPage) marca `pending`
  // na URL do Kit. Um acesso normal ao link não deve disparar polling.
  const isPending = searchParams.get('pending') === '1';

  // Initialize state based on token presence
  const [loading, setLoading] = useState(() => !!token);
  const [error, setError] = useState<string | null>(() => token ? null : 'Token não fornecido');
  const [data, setData] = useState<KitData | null>(null);
  // Enquanto true, documentos ainda indisponíveis mostram "Gerando documento…"
  // em vez de "Documento não disponível".
  const [polling, setPolling] = useState(isPending);

  useEffect(() => {
    if (!token) {
      return;
    }

    // `cancelled`/`timer` têm escopo desta execução do efeito (não do
    // componente): em StrictMode, que monta -> desmonta -> remonta em dev,
    // cada execução tem seu próprio par, então a execução antiga cancelada
    // nunca reaproveita o timer da execução nova nem passa pelo guard de uma
    // remontagem. Mesmo padrão usado em ClinicSlotSelector e ExamSelector.
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Loop recursivo via setTimeout: a próxima consulta só é agendada depois
    // que a anterior termina, então nunca há requisições empilhadas mesmo se
    // uma resposta demorar mais que KIT_POLL_INTERVAL_MS.
    const fetchKit = async (attempt: number) => {
      if (attempt === 0) {
        setLoading(true);
        setError(null);
      }

      const result = await kitService.getKitByToken(token);

      if (cancelled) {
        return;
      }

      if (result.valid && result.data) {
        setData(result.data);
        if (attempt === 0) {
          setLoading(false);
        }

        if (isPending && shouldKeepPolling(result.data.documents, attempt)) {
          timer = setTimeout(() => fetchKit(attempt + 1), KIT_POLL_INTERVAL_MS);
        } else {
          setPolling(false);
        }
        return;
      }

      if (result.error === 'EXPIRED') {
        navigate('/kit/expirado', { replace: true });
        return;
      }

      if (attempt === 0) {
        // Falha na carga inicial: mostra a tela de erro normalmente.
        setError(result.message ?? 'Erro ao carregar Kit de Atendimento');
        setLoading(false);
        setPolling(false);
        return;
      }

      // Erro numa tentativa de polling (ex.: rede instável) não deve derrubar
      // a tela nem interromper o polling — os dados já carregados permanecem
      // na tela e apenas tentamos de novo até o limite de tentativas (mesmo
      // orçamento de shouldKeepPolling, via canRetryAfterError).
      if (isPending && canRetryAfterError(attempt)) {
        timer = setTimeout(() => fetchKit(attempt + 1), KIT_POLL_INTERVAL_MS);
      } else {
        setPolling(false);
      }
    };

    fetchKit(0);

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
  }, [token, navigate, isPending]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }} color="text.secondary">
            Carregando Kit de Atendimento...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box bgcolor="background.default" minHeight="100vh" py={4}>
        {/* Mesma caixa do antigo <Container maxWidth="sm"> desta página pública sem cabeçalho */}
        <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 } }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <HospitalIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Erro ao Carregar
            </Typography>
            <Alert severity="error" sx={{ mt: 2 }}>
              {error || 'Dados não disponíveis'}
            </Alert>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box bgcolor="background.default" minHeight="100vh" py={4}>
      {/* Mesma caixa do antigo <Container maxWidth="md"> desta página pública sem cabeçalho */}
      <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <HospitalIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Kit de Atendimento
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Documentos do seu agendamento ocupacional
          </Typography>
        </Box>

        {/* Expiration Banner */}
        <ExpirationBanner expiresAt={data.expiresAt} />

        {/* Appointment Info Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon color="primary" />
              Dados do Agendamento
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={3}>
              {/* Patient */}
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <PersonIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Paciente
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.employee.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CPF: {data.employee.cpf}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Company */}
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <BusinessIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Empresa
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.company.name}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Exam Type */}
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <HospitalIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Tipo de Exame
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {data.examType}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Date and Time */}
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <TimeIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Data e Horário
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {kitService.formatDate(data.appointment.date)} as{' '}
                      {kitService.formatTime(data.appointment.hour, data.appointment.minute)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Clinic */}
              {data.clinic && (
                <Grid size={12}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <HospitalIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Clínica
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {data.clinic.name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Documents Section */}
        <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
          Documentos Disponíveis
        </Typography>

        <Grid container spacing={2}>
          {data.documents.map((doc) => (
            <Grid
              key={doc.type}
              size={{
                xs: 12,
                sm: 4
              }}>
              <KitDocumentCard
                document={doc}
                token={token!}
                processing={polling && !doc.available}
              />
            </Grid>
          ))}
        </Grid>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Este link é para uso exclusivo do paciente.
            <br />
            Não compartilhe com terceiros.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default KitAtendimentoPublicPage;
